import { apiClient } from './apiClient';

// Helper functions to convert date formats
const convertDateToISO = (dateStr: string): string => {
  if (!dateStr) return dateStr;

  // If already in ISO format (YYYY-MM-DD), return as is
  if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
    return dateStr;
  }

  // Convert from DD.MM.YYYY to YYYY-MM-DD
  if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
    const parts = dateStr.split('.');
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }

  // Try other common formats
  const date = new Date(dateStr);
  if (!isNaN(date.getTime())) {
    return date.toISOString().split('T')[0];
  }

  return dateStr;
};

// Helper functions to map backend field names to frontend field names
const mapChildFromBackend = (child: any): Child => ({
  ...child,
  birthDate: child.birthday || child.birthDate,
});

const mapPetFromBackend = (pet: any): Pet => ({
  ...pet,
  birthDate: pet.birthday || pet.birthDate,
});

export interface UserProfile {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  branchId?: string;
  patientClinicId?: string;
  points?: number;
  clinicId?: string;
}

export interface BalanceHistory {
  id: string;
  userId: string;
  pointsCount: number;
  transactionType: 'accrual' | 'write-off';
  comment?: string;
  balance?: number;
  createdAt: string;
}

export interface ChildBalance {
  id: string;
  name: string;
  balance: number;
}

export interface Child {
  id: string;
  name: string;
  birthDate?: string;
  branchId?: string;
}

export interface Pet {
  id: string;
  name: string;
  breed?: string;
  birthDate?: string;
  branchId?: string;
}

export interface Branch {
  id: string;
  name: string;
  address?: string;
  phone?: string;
}

export interface ClinicFeatures {
  pets?: boolean;
  files?: boolean;
  balance?: boolean;
  editPet?: boolean;
  addChild?: boolean;
  documents?: boolean;
  bonusSystem?: boolean;
  requestPapers?: boolean;
  personalCabinet?: boolean;
  matchCardEnabled?: boolean;
  connectWithManager?: boolean;
  requestCertificate?: boolean;
  changeBranchEnabled?: boolean;
  syncSpecializations?: boolean;
  connectWithAssistant?: boolean;
  canDisableUserBirthday?: boolean;
  recommendClinicEnabled?: boolean;
  approveAppointmentEnabled?: boolean;
}

export const getProfile = (): Promise<{ user: UserProfile; isMatched: boolean; hasPhone: boolean }> =>
  apiClient.get('/webapp/user/me');

export const updateProfile = (data: { name?: string }): Promise<{ user: UserProfile }> =>
  apiClient.put('/webapp/user/me', data);

export const matchCard = (data: { patientId?: string; phone?: string; birthday?: string }) =>
  apiClient.post('/webapp/user/me/match-card', data);

export const getBranches = (): Promise<{ branches: Branch[]; currentBranchId: string }> =>
  apiClient.get('/webapp/user/me/branches');

export const changeBranch = (branchId: string): Promise<{ success: boolean }> =>
  apiClient.post('/webapp/user/me/branch', { branchId });

export const getClinicFeatures = (): Promise<{ features: ClinicFeatures }> =>
  apiClient.get('/webapp/clinic/features').catch(() => ({ features: {} }));

export const getBalance = (): Promise<{ balance: number }> =>
  apiClient.get('/webapp/balance');

export const getBalanceHistory = (): Promise<{ history: BalanceHistory[] }> =>
  apiClient.get('/webapp/balance/history');

export const getChildrenBalances = (): Promise<{ children: ChildBalance[] }> =>
  apiClient.get('/webapp/balance/children');

export const getChildren = async (): Promise<{ children: Child[] }> => {
  try {
    const response = await apiClient.get('/webapp/children');
    return {
      children: response.children.map(mapChildFromBackend),
    };
  } catch {
    return { children: [] };
  }
};

export const addChild = async (data: { name: string; birthDate: string; branchId?: string }): Promise<{ child: Child }> => {
  const payload: any = { ...data };
  if (payload.birthDate) {
    payload.birthday = convertDateToISO(payload.birthDate);
    delete payload.birthDate;
  }
  const response = await apiClient.post('/webapp/children', payload);
  return {
    child: mapChildFromBackend(response.child),
  };
};

export const updateChild = async (childId: string, data: { name?: string; birthDate?: string; branchId?: string }): Promise<{ child: Child }> => {
  const payload: any = { ...data };
  if (payload.birthDate) {
    payload.birthday = convertDateToISO(payload.birthDate);
    delete payload.birthDate;
  }
  const response = await apiClient.put(`/webapp/children/${childId}`, payload);
  return {
    child: mapChildFromBackend(response.child),
  };
};

export const deleteChild = (childId: string): Promise<{ success: boolean }> =>
  apiClient.delete(`/webapp/children/${childId}`);

export const getPets = async (): Promise<{ pets: Pet[] }> => {
  try {
    const response = await apiClient.get('/webapp/pets');
    return {
      pets: response.pets.map(mapPetFromBackend),
    };
  } catch {
    return { pets: [] };
  }
};

export const addPet = async (data: { name: string; breed?: string; birthDate: string; branchId?: string }): Promise<{ pet: Pet }> => {
  const payload: any = { ...data };
  if (payload.birthDate) {
    payload.birthday = convertDateToISO(payload.birthDate);
    delete payload.birthDate;
  }
  const response = await apiClient.post('/webapp/pets', payload);
  return {
    pet: mapPetFromBackend(response.pet),
  };
};

export const updatePet = async (petId: string, data: { name?: string; breed?: string; birthDate?: string; branchId?: string }): Promise<{ pet: Pet }> => {
  const payload: any = { ...data };
  if (payload.birthDate) {
    payload.birthday = convertDateToISO(payload.birthDate);
    delete payload.birthDate;
  }
  const response = await apiClient.put(`/webapp/pets/${petId}`, payload);
  return {
    pet: mapPetFromBackend(response.pet),
  };
};

export const deletePet = (petId: string): Promise<{ success: boolean }> =>
  apiClient.delete(`/webapp/pets/${petId}`);

/**
 * Загружает все данные профиля за один запрос (оптимизировано для производительности)
 */
export const getAllProfileData = (): Promise<{
  user: UserProfile;
  branches: Branch[];
  children: Child[];
  pets: Pet[];
  balanceHistory: BalanceHistory[];
  balances: { parentBalance: number; children: ChildBalance[] };
  isMatched: boolean;
  hasPhone: boolean;
  currentBranchId: string;
}> =>
  apiClient.get('/webapp/user/me/all');
